import { Box, Icon, IconButton, Paragraph } from '@zillow/constellation-10';
import { IconNotePlusFilled } from '@zillow/constellation-icons';
import { Bone } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import { useContext } from 'react';
import { ModalType } from '../../../../types/ModalType';
import {
  emitNotesButtonClickEvent,
  NOTES_BUTTON_CLICK_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/notesModal';
import useModal from '../../../../hooks/useModal';
import { useConversationMessagesQuery } from '../../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import NotesModal from '../../../Modals/NotesModal';
import TourStatusCard from '../../../TourStatusCard';
import { useAuth } from '../../../../AuthProvider';
import ReadMoreText from '../../../ReadMoreText';

export const PLACEHOLDER_TEXT = 'Add note e.g. tour feedback';
export const TIMESTAMP_FORMAT_PATTERN = 'MMM d, h:mm a';

export type NoteSectionData = {
  isApplicationAlreadySent: boolean;
  isApplicationMarkedSent: boolean;
  isZrmToursEnabled: boolean;
  noteContent: string;
  renterName: string;
  tourScheduledTimestampMs?: number;
};

export const selectNoteSectionData = (data: ConversationMessagesData): NoteSectionData => ({
  isApplicationAlreadySent: data.note.isApplicationAlreadySent,
  isApplicationMarkedSent: data.note.isApplicationMarkedSent,
  isZrmToursEnabled: data.listingDetails.isZrmToursEnabled,
  noteContent: data.note.note ?? '',
  renterName: data.renterName ?? '',
  tourScheduledTimestampMs: data.note.tourScheduledTimestampMs,
});

const NoteSection = (): JSX.Element | null => {
  const { conversationId, clientTimeZone, listingAlias } = useContext(InboxPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();

  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: notesData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectNoteSectionData,
  });

  if (!notesData) {
    return <NoteSectionSkeleton />;
  }
  let tourScheduledTag = '',
    applicationSentTag = '';
  const {
    isApplicationAlreadySent,
    isApplicationMarkedSent,
    isZrmToursEnabled,
    noteContent,
    renterName,
    tourScheduledTimestampMs,
  } = notesData;
  // `!!` (not `!= null`) so a 0/epoch timestamp counts as "no tour", matching the prior behavior.
  const hasPersonallyNotedATour = !!tourScheduledTimestampMs;
  const shouldShowPersonallyNotedTour = hasPersonallyNotedATour && !isZrmToursEnabled;
  const shouldShowTags = isApplicationAlreadySent || isApplicationMarkedSent || shouldShowPersonallyNotedTour;
  const shouldShowTourStatusCard = isZrmToursEnabled && listingAlias && conversationId;

  if (isApplicationAlreadySent) {
    applicationSentTag = 'Application sent using Zillow';
  } else if (isApplicationMarkedSent) {
    applicationSentTag = 'Application sent';
  }

  if (shouldShowPersonallyNotedTour && tourScheduledTimestampMs != null) {
    const formattedTimestamp = format(tourScheduledTimestampMs, TIMESTAMP_FORMAT_PATTERN);
    tourScheduledTag = `Tour scheduled ${formattedTimestamp}`;
  }

  const handleNotesSectionClick = (): void => {
    emitNotesButtonClickEvent(user, { listingAlias });
    setActiveModal(ModalType.NotesModal);
  };

  return (
    <>
      {activeModal === ModalType.NotesModal && conversationId && listingAlias && (
        <NotesModal
          clientTimeZone={clientTimeZone}
          conversationId={conversationId}
          listingAlias={listingAlias}
          shouldRefetchStatusLabels={true}
        />
      )}

      <Box css={{ marginBlock: 'loose' }}>
        <Box css={{ marginBottom: 'default' }}>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
            }}
          >
            <Paragraph
              textStyle="body-sm-bold"
              css={{
                color: 'text.subtle',
              }}
            >
              Notes
            </Paragraph>
            <IconButton
              data-eventid={NOTES_BUTTON_CLICK_EVENT_TYPE_ID}
              onClick={handleNotesSectionClick}
              aria-haspopup="dialog"
              emphasis="bare"
              shape="square"
              size="md"
              title="Add note"
              tone="brand"
            >
              <Icon>
                <IconNotePlusFilled />
              </Icon>
            </IconButton>
          </Box>

          {noteContent ? (
            <ReadMoreText text={noteContent} maxLines={4} />
          ) : (
            <Paragraph css={{ color: 'text.subtle' }}>{PLACEHOLDER_TEXT}</Paragraph>
          )}
        </Box>

        {shouldShowTags && (
          <>
            <Paragraph
              textStyle="body-sm-bold"
              css={{
                color: 'text.subtle',
              }}
            >
              Tags
            </Paragraph>

            {applicationSentTag && <Paragraph css={{ marginBlock: 'tighter' }}>{applicationSentTag}</Paragraph>}
            {tourScheduledTag && <Paragraph css={{ marginBlock: 'tighter' }}>{tourScheduledTag}</Paragraph>}
          </>
        )}

        {shouldShowTourStatusCard && (
          <Box css={{ marginTop: 'default' }}>
            <TourStatusCard
              conversationId={conversationId}
              listingAlias={listingAlias}
              renterName={renterName}
              showStackedView={true}
              showViewInCalendarLink={true}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default NoteSection;

export const NoteSectionSkeleton = () => (
  <Box css={{ marginBlock: 'loose' }}>
    <>
      <Bone type="line" css={{ width: '33.33%', marginBottom: 'default' }} />
      <Bone type="line" css={{ marginBottom: 'default' }} />
      <Bone type="line" css={{ width: '58.33%', marginBottom: 'default' }} />
    </>
  </Box>
);
